import type {
    ConnectedPosition,
    FlexibleConnectedPositionStrategyOrigin,
} from '@angular/cdk/overlay';
import {
    DestroyRef,
    inject,
    InjectionToken,
    runInInjectionContext,
    signal,
    type Injector,
    type Provider,
    type Signal,
    type Type,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { assertInjector } from '@quipex/core/utils';
import { EMPTY, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';
import type {
    ComponentProperties,
    DialogResult,
} from '../../components/dialog';
import { HoverWarmSession } from '../hover-warm-session';
import { injectPopover } from './inject-popover';
import {
    popoverContainsTarget,
    setPopoverPointerBridge,
} from './popover-ownership';
import type { PopoverRef } from './popover-ref';

type HoverAction = 'cancel' | 'close' | 'open';

export const DEFAULT_POPOVER_OPEN_DELAY_MS = 500;
export const DEFAULT_POPOVER_CLOSE_DELAY_MS = 150;

export interface PopoverHoverContext {
    onMouseEnter(): void;
    onMouseLeave(relatedTarget: EventTarget | null): void;
}

export const QPX_POPOVER_HOVER_CONTEXT =
    new InjectionToken<PopoverHoverContext>('QPX_POPOVER_HOVER_CONTEXT');

export interface InjectPopoverWithHoverOptions<
    TComponent,
    TResult extends DialogResult = DialogResult,
> {
    readonly backdropClass?: string;
    readonly closeDelayMs?: number;
    readonly closeOnOutsidePointer?: boolean;
    readonly componentInputs?: () => Partial<ComponentProperties<TComponent>>;
    readonly isEnabled?: () => boolean;
    readonly loadComponent: () => Promise<Type<TComponent>>;
    readonly openBehavior?: 'preserve' | 'replace';
    readonly onResult?: (result: TResult) => void;
    readonly openDelayMs?: number;
    readonly origin?: (
        triggerOrigin: HTMLElement
    ) => FlexibleConnectedPositionStrategyOrigin;
    readonly overlaySelector: string;
    readonly panelClass?: string | readonly string[];
    readonly positions?: ConnectedPosition[];
    readonly providers?: Provider[];
    readonly push?: boolean;
    readonly triggerSelector: string;
}

export interface PopoverWithHoverRef<
    TResult extends DialogResult = DialogResult,
> {
    close(): void;
    enterTrigger(origin: HTMLElement): void;
    leaveTrigger(relatedTarget: EventTarget | null): void;
    readonly isOpen: Signal<boolean>;
    toggle(origin: HTMLElement): void;
}

export function injectPopoverWithHover<
    TComponent,
    TResult extends DialogResult = DialogResult,
>(
    options: InjectPopoverWithHoverOptions<TComponent, TResult>,
    injector?: Injector
): PopoverWithHoverRef<TResult> {
    const resolvedInjector = assertInjector(injectPopoverWithHover, injector);

    return runInInjectionContext(resolvedInjector, () => {
        const destroyRef = inject(DestroyRef);
        const parentHoverContext = inject(QPX_POPOVER_HOVER_CONTEXT, {
            optional: true,
        });
        const warmth = inject(HoverWarmSession);
        const isOpen = signal(false);
        const popover = injectPopover(resolvedInjector);
        const openDelayMs =
            options.openDelayMs ?? DEFAULT_POPOVER_OPEN_DELAY_MS;
        const closeDelayMs =
            options.closeDelayMs ?? DEFAULT_POPOVER_CLOSE_DELAY_MS;
        let activeOrigin: HTMLElement | null = null;
        let pendingAction: Exclude<HoverAction, 'cancel'> = 'open';
        let pointerBridge: HTMLElement | null = null;
        let pointerBridgeResizeObserver: ResizeObserver | null = null;
        let popoverRef: PopoverRef<TResult> | null = null;
        const actions$ = new Subject<HoverAction>();

        const removePointerBridge = (): void => {
            pointerBridge?.remove();
            pointerBridge = null;
            if (popoverRef) setPopoverPointerBridge(popoverRef, null);
        };

        const stopPointerBridgeTracking = (): void => {
            pointerBridgeResizeObserver?.disconnect();
            pointerBridgeResizeObserver = null;
        };

        const ownsPointerTarget = (target: EventTarget | null): boolean =>
            isWithinActiveOrigin(target, activeOrigin) ||
            (popoverRef
                ? popoverContainsTarget(popoverRef, target)
                : isWithinSelector(target, options.overlaySelector)) ||
            isWithinElement(target, pointerBridge);

        const createPointerBridge = (overlayElement: HTMLElement): void => {
            removePointerBridge();
            if (!activeOrigin) return;

            const originRect = activeOrigin.getBoundingClientRect();
            const overlayRect = overlayElement.getBoundingClientRect();
            const bridgeBounds = getPointerBridgeBounds(
                originRect,
                overlayRect
            );
            if (!bridgeBounds) return;

            const bridgeLayer = overlayElement.closest(
                '.cdk-overlay-container'
            );
            if (!(bridgeLayer instanceof HTMLElement)) return;

            const bridge = overlayElement.ownerDocument.createElement('div');
            bridge.setAttribute('aria-hidden', 'true');
            bridge.dataset['qpxHoverPointerBridge'] = '';
            Object.assign(bridge.style, {
                height: `${bridgeBounds.height}px`,
                left: `${bridgeBounds.left}px`,
                pointerEvents: 'auto',
                position: 'fixed',
                top: `${bridgeBounds.top}px`,
                width: `${bridgeBounds.width}px`,
                zIndex: '1000',
            });
            bridge.addEventListener('mouseenter', () => {
                hoverContext.onMouseEnter();
            });
            bridge.addEventListener('mouseleave', (event: MouseEvent) => {
                if (ownsPointerTarget(event.relatedTarget)) {
                    actions$.next('cancel');
                    return;
                }

                hoverContext.onMouseLeave(event.relatedTarget);
            });
            bridgeLayer.append(bridge);
            pointerBridge = bridge;
            if (popoverRef) setPopoverPointerBridge(popoverRef, bridge);
        };

        const closePopover = (): void => {
            actions$.next('cancel');
            popoverRef?.close();

            if (!popoverRef) {
                isOpen.set(false);
            }
        };

        const hoverContext: PopoverHoverContext = {
            onMouseEnter(): void {
                parentHoverContext?.onMouseEnter();
                actions$.next('cancel');
            },
            onMouseLeave(relatedTarget: EventTarget | null): void {
                parentHoverContext?.onMouseLeave(relatedTarget);
                if (ownsPointerTarget(relatedTarget)) {
                    actions$.next('cancel');
                    return;
                }

                requestAction('close');
            },
        };

        destroyRef.onDestroy(() => {
            actions$.next('cancel');
            actions$.complete();
            activeOrigin = null;
            stopPointerBridgeTracking();
            removePointerBridge();
            popoverRef?.close();
            popoverRef = null;
            isOpen.set(false);
        });

        const openPopover = (warmOnAttach: boolean): void => {
            if (popoverRef || !activeOrigin || !isEnabled(options.isEnabled)) {
                return;
            }

            const warmOwner = {};
            let ownsWarmth = false;
            const nextPopoverRef = popover.open<TComponent, TResult>({
                backdropClass: options.backdropClass,
                closeOnOutsidePointer: options.closeOnOutsidePointer ?? true,
                componentInputs: options.componentInputs?.(),
                loadComponent: options.loadComponent,
                onAttached: ({ geometryChanges, overlayElement }) => {
                    createPointerBridge(overlayElement);
                    geometryChanges
                        .pipe(
                            takeUntil(nextPopoverRef.afterClosed()),
                            takeUntilDestroyed(destroyRef)
                        )
                        .subscribe(() => {
                            createPointerBridge(overlayElement);
                        });
                    if (typeof ResizeObserver !== 'undefined') {
                        pointerBridgeResizeObserver = new ResizeObserver(() => {
                            createPointerBridge(overlayElement);
                        });
                        pointerBridgeResizeObserver.observe(overlayElement);
                    }
                    if (warmOnAttach) {
                        ownsWarmth = true;
                        warmth.opened(warmOwner);
                    }
                },
                openBehavior:
                    options.openBehavior ??
                    (parentHoverContext ? 'preserve' : undefined),
                origin: options.origin?.(activeOrigin) ?? activeOrigin,
                panelClass: options.panelClass,
                positions: options.positions,
                providers: [
                    ...(options.providers ?? []),
                    {
                        provide: QPX_POPOVER_HOVER_CONTEXT,
                        useValue: hoverContext,
                    },
                ],
                push: options.push,
            });

            popoverRef = nextPopoverRef;
            isOpen.set(true);

            nextPopoverRef
                .afterClosed()
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe((result: TResult) => {
                    if (popoverRef !== nextPopoverRef) return;

                    if (ownsWarmth) {
                        warmth.closed(warmOwner);
                    }
                    stopPointerBridgeTracking();
                    removePointerBridge();
                    popoverRef = null;
                    isOpen.set(false);
                    options.onResult?.(result);
                });
        };

        actions$
            .pipe(
                switchMap((action) => {
                    switch (action) {
                        case 'cancel':
                            return EMPTY;
                        case 'open':
                            return timer(warmth.hoverDelay(openDelayMs));
                        case 'close':
                            return timer(warmth.isWarm() ? 0 : closeDelayMs);
                    }
                }),
                tap(() => {
                    if (pendingAction === 'open') {
                        openPopover(true);
                        return;
                    }

                    closePopover();
                }),
                takeUntilDestroyed(destroyRef)
            )
            .subscribe();

        const requestAction = (
            action: Exclude<HoverAction, 'cancel'>
        ): void => {
            pendingAction = action;
            actions$.next(action);
        };

        return {
            close(): void {
                closePopover();
            },
            enterTrigger(origin: HTMLElement): void {
                if (!isEnabled(options.isEnabled)) return;

                const shouldReanchor =
                    popoverRef !== null && activeOrigin !== origin;
                activeOrigin = origin;

                if (shouldReanchor) {
                    closePopover();
                    openPopover(true);
                    return;
                }

                requestAction('open');
            },
            leaveTrigger(relatedTarget: EventTarget | null): void {
                if (ownsPointerTarget(relatedTarget)) {
                    actions$.next('cancel');
                    return;
                }

                requestAction('close');
            },
            isOpen: isOpen.asReadonly(),
            toggle(origin: HTMLElement): void {
                if (!isEnabled(options.isEnabled)) return;

                activeOrigin = origin;

                if (isOpen()) {
                    closePopover();
                    return;
                }

                openPopover(false);
            },
        };
    });
}

function isEnabled(isEnabledFn?: () => boolean): boolean {
    return isEnabledFn?.() ?? true;
}

function isWithinSelector(
    target: EventTarget | null,
    selector: string
): boolean {
    return target instanceof Element && target.closest(selector) != null;
}

function isWithinActiveOrigin(
    target: EventTarget | null,
    activeOrigin: HTMLElement | null
): boolean {
    return (
        activeOrigin !== null &&
        target instanceof Node &&
        (target === activeOrigin || activeOrigin.contains(target))
    );
}

function isWithinElement(
    target: EventTarget | null,
    element: Element | null
): boolean {
    return (
        element !== null &&
        target instanceof Node &&
        (target === element || element.contains(target))
    );
}

interface PointerBridgeBounds {
    readonly height: number;
    readonly left: number;
    readonly top: number;
    readonly width: number;
}

function getPointerBridgeBounds(
    originRect: DOMRect,
    overlayRect: DOMRect
): PointerBridgeBounds | null {
    const overlapLeft = Math.max(originRect.left, overlayRect.left);
    const overlapRight = Math.min(originRect.right, overlayRect.right);

    if (overlapRight > overlapLeft && originRect.bottom <= overlayRect.top) {
        return {
            height: overlayRect.top - originRect.bottom,
            left: overlapLeft,
            top: originRect.bottom,
            width: overlapRight - overlapLeft,
        };
    }

    if (overlapRight > overlapLeft && overlayRect.bottom <= originRect.top) {
        return {
            height: originRect.top - overlayRect.bottom,
            left: overlapLeft,
            top: overlayRect.bottom,
            width: overlapRight - overlapLeft,
        };
    }

    const overlapTop = Math.max(originRect.top, overlayRect.top);
    const overlapBottom = Math.min(originRect.bottom, overlayRect.bottom);

    if (overlapBottom > overlapTop && originRect.right <= overlayRect.left) {
        return {
            height: overlapBottom - overlapTop,
            left: originRect.right,
            top: overlapTop,
            width: overlayRect.left - originRect.right,
        };
    }

    if (overlapBottom > overlapTop && overlayRect.right <= originRect.left) {
        return {
            height: overlapBottom - overlapTop,
            left: overlayRect.right,
            top: overlapTop,
            width: originRect.left - overlayRect.right,
        };
    }

    return null;
}
